import { lazy, Suspense, useState } from 'react';
import { AdminPageHeader } from '../layouts/admin-page-header.js';
import { AdminContent } from '../layouts/admin-content.js';
import { AdminDateRangeSelector } from '../components/primitives/admin-date-range-selector.js';
import { AdminRefreshButton } from '../components/primitives/admin-refresh-button.js';
import { AdminErrorState } from '../components/primitives/admin-error-state.js';
import { AdminLoadingState } from '../components/primitives/admin-loading-state.js';
import { KpiGrid } from '../components/widgets/kpi-grid.js';
import { RecentOrdersList } from '../components/widgets/recent-orders-list.js';
import { LowStockAlerts } from '../components/widgets/low-stock-alerts.js';
import { TopSellingProducts } from '../components/widgets/top-selling-products.js';
import {
  NotificationsSummaryCard,
  PaymentsSummaryCard,
  RewardsSummaryCard,
  SystemHealthSummaryCard,
} from '../components/widgets/operations-summaries.js';
import { useDashboardOverview } from '../hooks/use-dashboard-overview.js';
import { formatDateTime } from '../lib/format.js';
import { AdminApiError } from '../api/admin-client.js';
import type { DashboardOverviewParams } from '../api/dashboard-api.js';

// Recharts (and every chart built on it) is lazy-loaded -- it is the
// single largest contributor to the initial bundle, and nothing above
// the fold (KPIs, recent orders, low-stock alerts) needs it to render.
// `Suspense`'s fallback reuses the same `AdminLoadingState` every other
// widget uses, so a slow chunk load looks identical to a slow data load
// (Part 17 "lazy-loaded chart code if useful").
const RevenueChart = lazy(() =>
  import('../components/widgets/revenue-chart.js').then((m) => ({ default: m.RevenueChart })),
);
const OrdersChart = lazy(() =>
  import('../components/widgets/orders-chart.js').then((m) => ({ default: m.OrdersChart })),
);
const NewCustomersChart = lazy(() =>
  import('../components/widgets/new-customers-chart.js').then((m) => ({ default: m.NewCustomersChart })),
);
const StatusDistributionChart = lazy(() =>
  import('../components/widgets/status-distribution-chart.js').then((m) => ({
    default: m.StatusDistributionChart,
  })),
);

function ChartFallback() {
  return <AdminLoadingState label="Loading chart" />;
}

const DEFAULT_PARAMS: DashboardOverviewParams = { preset: '7d' };

/**
 * The Milestone 1 overview page -- the only real admin page this milestone
 * ships (Part 15). A single failed widget never blanks the whole page
 * (Part 14): every chart/list/summary card owns its own loading/empty/
 * error rendering and reads from the same one overview query, so a
 * request-level failure surfaces once, clearly, with a retry, rather than
 * as a dead page.
 */
export function DashboardPage() {
  const [params, setParams] = useState<DashboardOverviewParams>(DEFAULT_PARAMS);
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } =
    useDashboardOverview(params);

  const errorMessage =
    error instanceof AdminApiError ? error.message : 'Something went wrong loading the dashboard.';

  return (
    <AdminContent>
      <AdminPageHeader
        title="Dashboard"
        description="An at-a-glance overview of store activity for the selected period."
        breadcrumbs={[{ label: 'Dashboard' }]}
        actions={
          <AdminRefreshButton
            onRefresh={() => void refetch()}
            isRefreshing={isFetching}
            lastUpdatedLabel={dataUpdatedAt ? `Updated ${formatDateTime(new Date(dataUpdatedAt).toISOString())}` : undefined}
          />
        }
      />

      <div className="mb-6">
        <AdminDateRangeSelector
          preset={params.preset}
          from={params.from}
          to={params.to}
          onChange={(value) => setParams(value)}
        />
      </div>

      {isError && !data ? (
        <AdminErrorState
          title="Could not load the dashboard"
          message={errorMessage}
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="space-y-6">
          <KpiGrid overview={data} isLoading={isLoading} />

          <Suspense fallback={<ChartFallback />}>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <RevenueChart
                series={data?.revenue.series ?? []}
                isLoading={isLoading}
                isError={isError}
                onRetry={() => void refetch()}
              />
              <OrdersChart
                series={data?.orders.series ?? []}
                isLoading={isLoading}
                isError={isError}
                onRetry={() => void refetch()}
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <StatusDistributionChart
                title="Order status distribution"
                description="Orders placed in the selected period, by status"
                byStatus={data?.orders.byStatus ?? {}}
                isLoading={isLoading}
                isError={isError}
                onRetry={() => void refetch()}
                errorMessage="Order status data could not be loaded."
              />
              <StatusDistributionChart
                title="Payment status distribution"
                description="Payments created in the selected period, by status"
                byStatus={data?.payments.byStatus ?? {}}
                isLoading={isLoading}
                isError={isError}
                onRetry={() => void refetch()}
                errorMessage="Payment status data could not be loaded."
              />
            </div>

            <div className="mt-6">
              <NewCustomersChart
                series={data?.customers.series ?? []}
                isLoading={isLoading}
                isError={isError}
                onRetry={() => void refetch()}
              />
            </div>
          </Suspense>

          <div className="grid grid-cols-1 gap-4">
            <RecentOrdersList
              orders={data?.recentOrders ?? []}
              isLoading={isLoading}
              isError={isError}
              onRetry={() => void refetch()}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <LowStockAlerts
              items={data?.lowStockItems ?? []}
              isLoading={isLoading}
              isError={isError}
              onRetry={() => void refetch()}
            />
            <TopSellingProducts
              products={data?.topSellingProducts ?? []}
              isLoading={isLoading}
              isError={isError}
              onRetry={() => void refetch()}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <PaymentsSummaryCard payments={data?.payments} isLoading={isLoading} />
            <RewardsSummaryCard rewards={data?.rewards} isLoading={isLoading} />
            <NotificationsSummaryCard notifications={data?.notifications} isLoading={isLoading} />
            <SystemHealthSummaryCard systemHealth={data?.systemHealth} isLoading={isLoading} />
          </div>
        </div>
      )}
    </AdminContent>
  );
}
